<template>
  <Teleport to="body">
  <div class="modal-overlay">
    <div
      class="mobile-status-container"
      :class="{
        'small-modal': mode !== 'update',
        'reactivate-modal': mode === 'reactivate',
        'code-step-modal': mode === 'update' && updateStep === 'code',
      }"
      @click.stop
    >
      <template v-if="mode === 'update'">
        <div class="modal-header">
          <h1 class="mobile-status-title">Update Mobile Number</h1>
          <button class="close-btn" @click="handleClose">
            <img :src="closeFillIcon" alt="close" class="close-icon" />
          </button>
        </div>

        <div class="modal-body">
          <template v-if="updateStep === 'phone'">
            <div class="form-group">
              <label class="form-label">
                <span class="required">*</span> Mobile number (used to log in)
              </label>
              <div class="mobile-input-wrapper" :class="{ error: hasError }">
                <span class="country-code">+1</span>
                <input
                  :value="newMobile"
                  type="tel"
                  inputmode="numeric"
                  pattern="[0-9]*"
                  class="mobile-input"
                  placeholder="Enter new mobile number"
                  :maxlength="phoneLimit"
                  @input="handleMobileInput"
                  @blur="handleBlur"
                />
                <img v-if="hasError" :src="alertIcon" alt="error" class="error-icon-img" />
              </div>
              <span v-if="hasError" class="error-text">{{ errorMessage }}</span>
            </div>
          </template>

          <template v-else>
            <div class="form-group">
              <label class="form-label">
                <span class="required">*</span> Verification code
              </label>
              <div class="mobile-input-wrapper" :class="{ error: hasError }">
                <input
                  :value="verifyCode"
                  type="tel"
                  inputmode="numeric"
                  pattern="[0-9]*"
                  class="mobile-input"
                  placeholder="Enter verification code"
                  :maxlength="codeLimit"
                  @input="handleCodeInput"
                  @blur="handleCodeBlur"
                />
                <img v-if="hasError" :src="alertIcon" alt="error" class="error-icon-img" />
              </div>
              <span v-if="hasError" class="error-text">{{ errorMessage }}</span>
            </div>
          </template>
        </div>

        <div class="mobile-status-actions">
          <button
            v-if="updateStep === 'phone'"
            class="btn btn-next"
            :disabled="sendingCode"
            @click="handleNext"
          >
            <span v-if="sendingCode" class="spinner"></span>
            <span v-else>Next</span>
          </button>
          <template v-else>
            <button
              type="button"
              class="btn btn-no btn-resend"
              :disabled="countdown > 0 || sendingCode"
              @click="handleResend"
            >
              <span
                class="btn-resend-text"
                :class="{ 'btn-resend-text--countdown': countdown > 0 }"
              >
                {{ countdown > 0 ? `${countdown}s` : 'Resend' }}
              </span>
            </button>
            <button type="button" class="btn btn-no" @click="handleClose">Cancel</button>
            <button
              class="btn btn-next"
              :disabled="submitting"
              @click="handleSubmitCode"
            >
              <span v-if="submitting" class="spinner"></span>
              <span v-else>Submit</span>
            </button>
          </template>
        </div>
      </template>

      <template v-else-if="mode === 'deactivate'">
        <div class="modal-header">
          <h1 class="mobile-status-title">Deactivate User</h1>
          <button class="close-btn" @click="handleClose">
            <img :src="closeFillIcon" alt="close" class="close-icon" />
          </button>
        </div>

        <div class="modal-body deactivate-body">
          <p v-if="blockDeactivate" class="deactivate-text">
            {{ blockMessage }}
          </p>
          <p v-else class="deactivate-text">
            Are you sure you want to deactivate this account. This user cannot login to HomeMetric.
            You can reactivate it if needed.
          </p>
        </div>

        <div class="mobile-status-actions">
          <template v-if="blockDeactivate">
            <button class="btn btn-yes" @click="handleClose">Got it</button>
          </template>
          <template v-else>
            <button class="btn btn-no" @click="handleClose">No</button>
            <button class="btn btn-yes" :disabled="submitting" @click="handleConfirm">Yes</button>
          </template>
        </div>
      </template>

      <template v-else-if="mode === 'reactivate'">
        <div class="modal-header reactivate-header">
          <h1 class="mobile-status-title">Reactivate User</h1>
          <button class="close-btn" @click="handleClose">
            <img :src="closeFillIcon" alt="close" class="close-icon" />
          </button>
        </div>

        <div class="modal-body deactivate-body">
          <p class="deactivate-text reactivate-text">
            Are you sure you want to reactivate this account? It will remain available.
          </p>
        </div>

        <div class="mobile-status-actions">
          <button class="btn btn-no" @click="handleClose">No</button>
          <button class="btn btn-yes" :disabled="submitting" @click="handleConfirm">Yes</button>
        </div>
      </template>
    </div>
  </div>
  </Teleport>
</template>

<script setup lang="ts">
import { ref, watch, inject, onUnmounted } from 'vue'
import closeFillIcon from '@/assets/Fill 1@2x.png'
import alertIcon from '@/assets/alert.png'
import { sendUpdateUserMobileCode, updateUserMobile } from '@/api/usermobile'
import {
  updateUserStatus,
  isDeactivateBlockedByOrdersOrSubscriptions,
  type ApiResponse,
} from '@/api/userstatus'
import { getUserDetail } from '@/api/userdetail'
import { sanitizeDigitsOnly } from '@/utils/fieldLimitValidation'

const phoneLimit = 10
const codeLimit = 6
const RESEND_SECONDS = 60
const DEACTIVATE_BLOCK_FALLBACK =
  'This account still has unfinished orders or subscriptions and cannot be deactivated.'

const showToast = inject<(type: 'success' | 'warning' | 'error', message: string) => void>(
  'showToast',
  () => {},
)

const emit = defineEmits<{
  (e: 'close'): void
  (e: 'saved'): void
}>()

const props = defineProps<{
  userId: string
  mode: 'update' | 'deactivate' | 'reactivate'
  phone?: string
  phoneCode?: string
}>()

function extractPhoneNumber(phone: string, phoneCode?: string): string {
  if (!phone) return ''

  let digits = phone.replace(/[^\d]/g, '')
  const code = (phoneCode || '1').replace(/[^\d]/g, '')

  if (code && digits.startsWith(code) && digits.length > 10) {
    digits = digits.slice(code.length)
  } else if (digits.length === 11 && digits.startsWith('1')) {
    digits = digits.slice(1)
  }

  return digits.length > 10 ? digits.slice(-10) : digits
}

type UpdateStep = 'phone' | 'code'

const updateStep = ref<UpdateStep>('phone')
const newMobile = ref('')
const verifyCode = ref('')
const hasError = ref(false)
const errorMessage = ref('')
const submitting = ref(false)
const sendingCode = ref(false)
const countdown = ref(0)
/** Deactivate blocked: show API msg + Got it only. */
const blockDeactivate = ref(false)
const blockMessage = ref(DEACTIVATE_BLOCK_FALLBACK)
let countdownTimer: ReturnType<typeof setInterval> | null = null

function clearCountdown() {
  if (countdownTimer != null) {
    clearInterval(countdownTimer)
    countdownTimer = null
  }
  countdown.value = 0
}

function startCountdown() {
  clearCountdown()
  countdown.value = RESEND_SECONDS
  countdownTimer = setInterval(() => {
    if (countdown.value <= 1) {
      clearCountdown()
      return
    }
    countdown.value -= 1
  }, 1000)
}

function resetUpdateFlow() {
  updateStep.value = 'phone'
  verifyCode.value = ''
  hasError.value = false
  errorMessage.value = ''
  sendingCode.value = false
  submitting.value = false
  blockDeactivate.value = false
  blockMessage.value = DEACTIVATE_BLOCK_FALLBACK
  clearCountdown()
}

function applyDeactivateBlocked(msg?: string) {
  const trimmed = msg?.trim()
  blockMessage.value = trimmed || DEACTIVATE_BLOCK_FALLBACK
  blockDeactivate.value = true
}

async function loadCurrentPhone() {
  if (props.mode !== 'update') return

  const propPhone = props.phone?.trim()
  if (propPhone) {
    newMobile.value = extractPhoneNumber(propPhone, props.phoneCode)
    return
  }

  if (!props.userId) return

  try {
    const response = await getUserDetail(props.userId)
    if (response.code === 200 && response.data) {
      const data = response.data
      const rawPhone = data.mobile || data.phone || ''
      newMobile.value = extractPhoneNumber(rawPhone, data.phone_code)
    }
  } catch (error) {
    console.error('Failed to load user phone:', error)
  }
}

watch(
  () => [props.mode, props.phone, props.phoneCode, props.userId] as const,
  () => {
    resetUpdateFlow()
    if (props.mode === 'update') {
      void loadCurrentPhone()
    }
  },
  { immediate: true },
)

onUnmounted(() => {
  clearCountdown()
})

function handleClose() {
  resetUpdateFlow()
  emit('close')
}

function handleMobileInput(event: Event) {
  newMobile.value = sanitizeDigitsOnly((event.target as HTMLInputElement).value, phoneLimit)
  hasError.value = false
  errorMessage.value = ''
}

function handleCodeInput(event: Event) {
  verifyCode.value = sanitizeDigitsOnly((event.target as HTMLInputElement).value, codeLimit)
  hasError.value = false
  errorMessage.value = ''
}

function handleBlur() {
  const phone = newMobile.value.trim()
  const isValidPhone = /^\d{10}$/.test(phone)

  if (phone && !isValidPhone) {
    hasError.value = true
    errorMessage.value = 'Please enter a 10-digit mobile number'
  }
}

function handleCodeBlur() {
  const code = verifyCode.value.trim()
  if (code && !/^\d{6}$/.test(code)) {
    hasError.value = true
    errorMessage.value = 'Please enter a 6-digit verification code'
  }
}

function closeDelay() {
  setTimeout(() => {
    resetUpdateFlow()
    emit('close')
  }, 2100)
}

/** POST `/api/admin/user/send-update-mobile-code` */
async function sendCode(): Promise<void> {
  sendingCode.value = true
  try {
    const response = await sendUpdateUserMobileCode({
      id: Number(props.userId),
      phone_code: '1',
      phone: newMobile.value.trim(),
    })

    if (response.code === 200) {
      showToast('success', response.msg || 'Verification code sent')
      updateStep.value = 'code'
      verifyCode.value = ''
      hasError.value = false
      errorMessage.value = ''
      startCountdown()
      return
    }

    showToast('error', response.msg || 'Failed to send verification code')
  } catch (error) {
    console.error('Send verification code error:', error)
    showToast('error', 'Network request failed')
  } finally {
    sendingCode.value = false
  }
}

async function handleNext() {
  const phone = newMobile.value.trim()
  const isValidPhone = /^\d{10}$/.test(phone)

  if (!isValidPhone) {
    hasError.value = true
    errorMessage.value = 'Please enter a 10-digit mobile number'
    showToast('warning', 'Please enter valid phone number')
    return
  }

  hasError.value = false
  errorMessage.value = ''
  await sendCode()
}

async function handleResend() {
  if (countdown.value > 0 || sendingCode.value) return
  await sendCode()
}

/** POST `/api/admin/user/update-mobile` */
async function handleSubmitCode() {
  const code = verifyCode.value.trim()
  if (!/^\d{6}$/.test(code)) {
    hasError.value = true
    errorMessage.value = 'Please enter a 6-digit verification code'
    showToast('warning', 'Please enter a 6-digit verification code')
    return
  }

  hasError.value = false
  errorMessage.value = ''
  submitting.value = true

  try {
    const response = await updateUserMobile({
      id: Number(props.userId),
      phone_code: '1',
      phone: newMobile.value.trim(),
      sms_code: code,
    })

    if (response.code === 200) {
      showToast('success', 'Successfully updated')
      emit('saved')
      closeDelay()
    } else {
      const msg = response.msg || 'Mobile number already exist'
      hasError.value = true
      errorMessage.value = msg
      showToast('error', msg)
    }
  } catch (error) {
    console.error('Update mobile error:', error)
    const msg = 'Mobile number already exist'
    hasError.value = true
    errorMessage.value = msg
    showToast('error', msg)
  } finally {
    submitting.value = false
  }
}

async function handleConfirm() {
  if (props.mode === 'deactivate' && blockDeactivate.value) return

  submitting.value = true

  try {
    const status = props.mode === 'deactivate' ? 0 : 1

    const response = await updateUserStatus({
      id: Number(props.userId),
      status: status,
    })

    if (response.code === 200) {
      showToast(
        'success',
        props.mode === 'deactivate' ? 'Successfully deactivated' : 'Successfully reactivated',
      )
      emit('saved')
      closeDelay()
    } else if (
      props.mode === 'deactivate' &&
      isDeactivateBlockedByOrdersOrSubscriptions(response)
    ) {
      applyDeactivateBlocked(response.msg)
    } else {
      showToast('error', response.msg || 'Operation failed')
    }
  } catch (error) {
    console.error('Status update error:', error)
    if (
      props.mode === 'deactivate' &&
      error &&
      typeof error === 'object' &&
      'code' in error &&
      isDeactivateBlockedByOrdersOrSubscriptions(error as ApiResponse)
    ) {
      const errMsg =
        'msg' in error && typeof (error as ApiResponse).msg === 'string'
          ? (error as ApiResponse).msg
          : undefined
      applyDeactivateBlocked(errMsg)
    } else {
      showToast('error', 'Network request failed')
    }
  } finally {
    submitting.value = false
  }
}
</script>

<style scoped>
.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 10001;
  padding: 20px;
}

.mobile-status-container {
  background: #fff;
  border-radius: 12px;
  width: 530px;
  height: 236px;
  display: flex;
  flex-direction: column;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.2);
}

.mobile-status-container.code-step-modal {
  width: 530px;
  height: 236px;
  background: #ffffff;
  box-shadow: 0px 8px 16px 0px rgba(0, 0, 0, 0.1);
  border-radius: 16px;
  border: 1px solid #d8d8d8;
}

.mobile-status-container.small-modal.reactivate-modal {
  width: 530px;
  height: 172px;
}
.mobile-status-container.small-modal {
  width: 530px;
  height: 212px;
}
.mobile-status-container.small-modal .reactivate-header {
  padding-bottom: 24px;
}

.mobile-status-container.small-modal .reactivate-header + .modal-body {
  flex: none;
  padding: 0 24px;
}

.reactivate-modal .deactivate-text,
.reactivate-text {
  white-space: nowrap;
}

.reactivate-modal .mobile-status-actions {
  margin-top: 24px;
  padding: 0 24px 24px;
}

.modal-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 24px 32px 0 24px;
  flex-shrink: 0;
}

.small-modal .modal-header {
  padding: 24px;
}

.mobile-status-title {
  font-size: 16px;
  font-weight: 700;
  line-height: 24px;
  font-family:
    SFPro,
    'SF Pro',
    -apple-system,
    BlinkMacSystemFont,
    system-ui,
    sans-serif;
  color: #000000;
  margin: 0;
  text-align: left;
}

.small-modal .mobile-status-title {
  font-size: 16px;
  font-weight: 700;
  line-height: 24px;
}

.close-btn {
  width: 16px;
  height: 16px;
  padding: 0;
  border: none;
  background: transparent;
  border-radius: 4px;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  justify-content: center;
}

.close-icon {
  width: 16px;
  height: 16px;
}

.small-modal .close-icon {
  width: 16px;
  height: 16px;
}

.modal-body {
  flex: 1;
  overflow-y: auto;
  padding: 24px;
}

.small-modal .modal-body {
  padding-right: 24px;
  padding-left: 24px;
}

.modal-body::-webkit-scrollbar {
  width: 6px;
}

.modal-body::-webkit-scrollbar-track {
  background: #f1f1f1;
  border-radius: 3px;
}

.modal-body::-webkit-scrollbar-thumb {
  background: #c1c1c1;
  border-radius: 3px;
}

.modal-body::-webkit-scrollbar-thumb:hover {
  background: #a1a1a1;
}

.deactivate-body {
  padding: 0;
}

.deactivate-text {
  font-size: 14px;
  font-weight: 400;
  line-height: 20px;
  font-family:
    SFPro,
    'SF Pro',
    'SF Pro Text',
    -apple-system,
    BlinkMacSystemFont,
    system-ui,
    sans-serif;
  color: #000000;
  margin: 0;
}

.small-modal .deactivate-text {
  font-size: 14px;
  font-weight: 400;
  line-height: 20px;
  font-family:
    SFPro,
    'SF Pro',
    -apple-system,
    BlinkMacSystemFont,
    system-ui,
    sans-serif;
  color: #000000;
}

.form-group {
  margin-top: -16px;
  margin-bottom: 0;
}

.form-label {
  display: block;
  margin-bottom: 16px;
  font-size: 14px;
  font-weight: 400;
  line-height: 20px;
  font-family:
    SFPro,
    'SF Pro',
    'SF Pro Text',
    -apple-system,
    BlinkMacSystemFont,
    system-ui,
    sans-serif;
  color: #000000;
}

.required {
  color: #ff4d4f;
  margin-right: 4px;
}

.mobile-input-wrapper {
  width: 482px;
  height: 32px;
  display: flex;
  align-items: center;
  border: 1px solid #e0e0e0;
  border-radius: 16px;
  background: #fff;
  overflow: hidden;
  transition: all 0.2s ease;
}

.mobile-input-wrapper:focus-within {
  border-color: #666666;
}

.mobile-input-wrapper.error,
.mobile-input-wrapper.error:focus-within {
  border-color: #ff4d4f;
}

.country-code {
  position: relative;
  padding: 0 12px;
  font-size: 14px;
  font-weight: 400;
  line-height: 20px;
  font-family:
    SFPro,
    'SF Pro',
    'SF Pro Text',
    -apple-system,
    BlinkMacSystemFont,
    system-ui,
    sans-serif;
  color: #000000;
  height: 32px;
  display: flex;
  align-items: center;
  flex-shrink: 0;
}

.country-code::after {
  content: '';
  position: absolute;
  right: 0;
  top: 50%;
  transform: translateY(-50%);
  width: 1px;
  height: 16px;
  background-color: #e8e8e8;
}

.mobile-input {
  flex: 1;
  padding: 0 16px;
  border: none;
  outline: none;
  font-size: 14px;
  font-weight: 400;
  line-height: 20px;
  font-family:
    SFPro,
    'SF Pro',
    'SF Pro Text',
    -apple-system,
    BlinkMacSystemFont,
    system-ui,
    sans-serif;
  color: #000000;
  background: transparent;
  height: 32px;
}

.mobile-input::placeholder {
  font-size: 14px;
  font-weight: 400;
  line-height: 20px;
  font-family:
    SFPro,
    'SF Pro',
    -apple-system,
    BlinkMacSystemFont,
    system-ui,
    sans-serif;
  color: #cccccc;
  letter-spacing: normal;
}

.error-icon-img {
  width: 16px;
  height: 16px;
  margin-right: 12px;
  object-fit: contain;
  flex-shrink: 0;
}

.error-text {
  display: block;
  margin-top: 6px;
  font-size: 0.85rem;
  color: #ff4d4f;
}

.mobile-status-actions {
  display: flex;
  justify-content: flex-end;
  gap: 12px;
  padding: 0 24px 24px;
}

.small-modal .mobile-status-actions {
  gap: 8px;
  padding: 0 24px 24px;
}

.mobile-status-container.code-step-modal .mobile-status-actions {
  gap: 16px;
}

.btn {
  padding: 10px 24px;
  border-radius: 100px;
  font-size: 0.9rem;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;
}

.small-modal .btn {
  width: 80px;
  height: 32px;
  padding: 0;
  font-size: 14px;
  font-weight: 400;
  line-height: 20px;
  font-family:
    SFPro,
    'SF Pro',
    -apple-system,
    BlinkMacSystemFont,
    system-ui,
    sans-serif;
}

.btn-next {
  width: 80px;
  height: 32px;
  background: #000000;
  color: #fff;
  border-radius: 36px;
  display: flex;
  align-items: center;
  justify-content: center;
  position: relative;
  overflow: hidden;
  border: none;
  font-size: 14px;
  font-weight: 700;
  font-family:
    SFPro,
    'SF Pro',
    -apple-system,
    BlinkMacSystemFont,
    system-ui,
    sans-serif;
}

.btn-next:hover:not(:disabled) {
  width: 80px;
  height: 32px;
  background: #333;
}
.btn-next:disabled {
  opacity: 0.7;
}
.btn-next .spinner {
  width: 16px;
  height: 16px;
  border: 2px solid rgba(255, 255, 255, 0.3);
  border-top-color: #fff;
  border-radius: 50%;
  animation: spin 0.6s linear infinite;
}

@keyframes spin {
  to {
    transform: rotate(360deg);
  }
}

.btn-no {
  box-sizing: border-box;
  width: 80px;
  height: 32px;
  border: 1px solid #000000;
  background: #fff;
  color: #000000;
  border-radius: 16px;
  font-size: 14px;
  font-weight: 400;
  line-height: 20px;
  font-family:
    SFPro,
    'SF Pro',
    -apple-system,
    BlinkMacSystemFont,
    system-ui,
    sans-serif;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0;
}

.btn-resend {
  padding: 6px 15px 6px 16px;
  display: block;
}

.btn-resend-text {
  display: block;
  width: 49px;
  height: 20px;
  font-family: SFPro, SFPro, 'SF Pro', -apple-system, BlinkMacSystemFont, system-ui, sans-serif;
  font-weight: 400;
  font-size: 14px;
  color: inherit;
  line-height: 20px;
  text-align: right;
  font-style: normal;
}

.btn-resend-text--countdown {
  text-align: center;
}

.btn-no:hover:not(:disabled) {
  border-color: #000000;
  background-color: #000000;
  color: #ffffff;
}

.btn-no:disabled {
  border-color: #d0d0d0;
  color: #b0b0b0;
  background: #fff;
  cursor: not-allowed;
}

.btn-yes {
  width: 80px;
  height: 32px;
  border: 1px solid #000000;
  background: #000000;
  color: #fff;
  font-size: 14px;
  font-weight: 400;
  line-height: 20px;
  font-family:
    SFPro,
    'SF Pro',
    -apple-system,
    BlinkMacSystemFont,
    system-ui,
    sans-serif;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0;
}

.btn-yes:hover {
  background: #484848;
}

@media (max-width: 768px) {
  .modal-overlay {
    padding: 10px;
  }

  .modal-body {
    padding: 16px;
  }
}
</style>
